'use client';

import { createContext, ReactNode, useCallback, useContext } from 'react';
import { Amplify } from 'aws-amplify';
import { AuthError, fetchAuthSession, signOut } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';
import { CookieStorage } from 'aws-amplify/utils';
import { Authenticator } from '@aws-amplify/ui-react';
import { optOut as optOutAnalytics } from '@upms/analytics';
import { setTrackingConsent } from '@upms/observability';
import { amplifyConfig } from '@/config/amplify';
import { env } from '@/config/env';
import { useQueryClient } from '@tanstack/react-query';

Amplify.configure(amplifyConfig, { ssr: true });

cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: env.NEXT_PUBLIC_COOKIE_DOMAIN,
    secure: true,
    sameSite: 'lax',
    path: '/',
  })
);

interface AuthContextValues {
  clearSession: () => void;
  clearTokens: () => Promise<void>;
  getSession: () => Promise<{
    accountId: string;
    idToken: string;
    authTime: number | undefined;
  }>;
  getSessionHeaders: () => Promise<{
    authorization: string;
    'x-pie-account-id': string;
  }>;
}

const AuthContext = createContext<AuthContextValues>({} as AuthContextValues);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();

  const clearTokens = useCallback(
    () => cognitoUserPoolsTokenProvider.tokenOrchestrator.clearTokens(),
    []
  );

  const clearSession = useCallback(async () => {
    setTrackingConsent(false);
    optOutAnalytics();
    await signOut();
    await clearTokens();
    queryClient.clear();
    window.location.assign('/sign-in');
  }, [clearTokens, queryClient]);

  const getSession = useCallback(async () => {
    const session = await fetchAuthSession().catch((err: AuthError) => err);

    if (session instanceof AuthError || session.tokens?.idToken === undefined) {
      // must throw an error to prevent downstream requests
      throw new Error(`Unable to fetch or refresh the current session.`);
    }

    const accountId = session.tokens.idToken.payload?.['custom:account_id'] as string;
    const idToken = session.tokens.idToken.toString();
    const authTime = session.tokens.idToken.payload?.auth_time as number | undefined;

    return { accountId, idToken, authTime };
  }, []);

  const getSessionHeaders = useCallback(async () => {
    const { accountId, idToken } = await getSession();

    return {
      authorization: `Bearer ${idToken}`,
      'x-pie-account-id': accountId,
    };
  }, [getSession]);

  return (
    <Authenticator.Provider>
      <AuthContext.Provider
        value={{
          clearSession,
          clearTokens,
          getSession,
          getSessionHeaders,
        }}
      >
        {children}
      </AuthContext.Provider>
    </Authenticator.Provider>
  );
};

export const useAuthContext = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within <AuthProvider>');
  return ctx;
};

export const getSignInWithRedirect = () => {
  const redirect = `${window.location.pathname}${window.location.search}`;

  if (redirect !== '/') {
    return `/sign-in?redirect=${encodeURIComponent(redirect)}`;
  }

  return `/sign-in`;
};
